<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900">

<!-- Sidebar -->
<HrSidebar :isOpen="isSidebarOpen"/>

<main class="flex-1 transition-all duration-300" :class="{'ml-64':isSidebarOpen,'ml-0':!isSidebarOpen}">

<!-- Topbar -->
<header class="fixed top-0 w-full z-50 bg-white dark:bg-gray-800 shadow">
<HrTopbar
:page-title="currentPageTitle"
:is-sidebar-open="isSidebarOpen"
:is-dark-mode="isDarkMode"
@toggle-sidebar="toggleSidebar"
@toggle-dark-mode="toggleDarkMode"
/>
</header>

<div class="p-6 mt-16">

<!-- Search + Button -->
<div class="flex justify-between mb-8 p-4">

<input
v-model="search"
type="text"
placeholder="Search class..."
class="border rounded-lg px-4 py-2 w-72 dark:bg-gray-700 dark:text-white"
/>

<button
@click="openModal"
class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f]"
>
+ Add Class
</button>

</div>

<!-- Class Grid -->

<div v-if="filteredClasses.length" class="grid md:grid-cols-3 lg:grid-cols-4 gap-6">

<div
v-for="cls in filteredClasses"
:key="cls.id"
class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-lg"
>

<div class="flex items-center space-x-3 mb-4">

<div class="w-12 h-12 bg-secondary text-white flex items-center justify-center rounded-full font-bold">
{{ cls.code }}
</div>

<div>

<h3 class="font-semibold text-gray-800 dark:text-white">
{{ cls.name }}
</h3>

<span class="text-xs text-gray-500">
{{ getSectionName(cls.section_id) }}
</span>

</div>

</div>

<p class="text-sm text-gray-500 mb-6">
{{ cls.description }}
</p>

<div class="flex justify-between text-sm">

<button
@click="editClass(cls)"
class="text-blue-600 hover:underline"
>
Edit
</button>

<button
@click="deleteClass(cls.id)"
class="text-red-500 hover:underline"
>
Delete
</button>

</div>

</div>

</div>

<div v-else class="text-center text-gray-400 mt-20">
No classes created yet
</div>


<!-- Modal -->

<Transition name="modal">
<div
v-if="showModal"
class="fixed inset-0 bg-black/40 flex items-center justify-center"
>

<div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px]">

<h3 class="text-lg font-semibold text-[#035a76] mb-6">
{{ editing ? "Edit Class" : "Create Class" }}
</h3>

<form @submit.prevent="saveClass" class="space-y-4">

<input
v-model="form.name"
type="text"
placeholder="Class Name"
required
class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
/>

<input
v-model="form.code"
type="text"
placeholder="Code (G1, N1...)"
required
class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
/>

<select
v-model="form.section_id"
required
class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
>

<option disabled value="">Select Section</option>

<option
v-for="section in sections"
:key="section.id"
:value="section.id"
>
{{ section.name }}
</option>

</select>

<textarea
v-model="form.description"
placeholder="Description"
class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
></textarea>

<div class="flex justify-end space-x-3">

<button
type="button"
@click="closeModal"
class="px-4 py-2 bg-gray-300 rounded"
>
Cancel
</button>

<button
type="submit"
class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]"
>
Save
</button>

</div>

</form>

</div>

</div>
</Transition>

</div>

</main>
</div>
</template>

<script setup>

import {ref,computed,onMounted} from "vue"
import api from "@/services/api"
import HrSidebar from "@/components/hr/HrSidebar.vue"
import HrTopbar from "@/components/hr/HrTopbar.vue"

const currentPageTitle = ref("Classes")

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const showModal = ref(false)
const editing = ref(false)

const search = ref("")

const sections = ref([])

const classes = ref([])


const fetchSections = async () => {
  try{
    const response = await api.get("Sections.php");
    sections.value = response.data.map(section => ({
      id:section.id,
      name:section.name,
      code:section.code,
      description:section.description
    }));

  } catch (error) {
    console.error("Error fetching Sections:", error);
  }
};

const fetchClasses = async () => {
  try {
    const response = await api.get("Classes.php");

    classes.value = response.data.map(item => ({
      id: item.id,
      department_id: item.section_id,
      department_name: item.section_name,
      name: item.name,
      code: item.code,
      description: item.description
    }));

  } catch (error) {
    console.error("Error fetching Classes:", error);
  }
};

// const sections = ref([
// {id:1,name:"Nursery"},
// {id:2,name:"Primary"},
// {id:3,name:"Junior High"}
// ])

// const classes = ref([
// {
// id:1,
// name:"Nursery 1",
// code:"N1",
// section_id:1,
// description:"Nursery class level 1"
// },
// {
// id:2,
// name:"Grade 1",
// code:"G1",
// section_id:2,
// description:"Primary grade 1"
// }
// ])

const form = ref({
id:null,
name:"",
code:"",
section_id:"",
description:""
})

const filteredClasses = computed(()=>{
return classes.value.filter(c=>
c.name.toLowerCase().includes(search.value.toLowerCase())
)
})

const getSectionName=(id)=>{
const sec = sections.value.find(s=>s.id===id)
return sec ? sec.name : ""
}

const openModal=()=>{
editing.value=false
form.value={id:null,name:"",code:"",section_id:"",description:""}
showModal.value=true
}

const editClass=(cls)=>{
editing.value=true
form.value={...cls}
showModal.value=true
}

// const saveClass=()=>{

// if(editing.value){

// const index=classes.value.findIndex(c=>c.id===form.value.id)
// classes.value[index]={...form.value}

// }else{

// classes.value.push({
// id:Date.now(),
// ...form.value
// })

// }

// closeModal()

// }

const saveClass = async () => {
  try {
    let response;

    if (editing.value) {
      response = await api.put("Classes.php", form.value);

      const index = classes.value.findIndex(c => c.id === form.value.id);
      if (index !== -1) {
        classes.value[index] = { ...form.value };
      }

    } else {
      response = await api.post("Classes.php", form.value);

      classes.value.push({
        id: response.data.id,
        name: response.data.name,
        code: response.data.code,
        description: response.data.description,
        section_id: response.data.section_id
      });
    }

    closeModal();

  } catch (error) {
    console.error("Error saving class:", error);
  }
};


// const deleteClass=(id)=>{
// classes.value=classes.value.filter(c=>c.id!==id)
// }

const deleteClass = async (id) => {
  try {

    await api.delete("Classes.php", {
      data: { id: id }
    });

    classes.value = classes.value.filter(c => c.id !== id);

  } catch (error) {
    console.error("Error deleting class:", error);
  }
};

const closeModal=()=>{
showModal.value=false
}

const toggleSidebar=()=>{
isSidebarOpen.value=!isSidebarOpen.value
}

const toggleDarkMode=()=>{
isDarkMode.value=!isDarkMode.value

if(isDarkMode.value){
document.documentElement.classList.add("dark")
}else{
document.documentElement.classList.remove("dark")
}

}

onMounted(() => {
    fetchClasses();
    fetchSections();
})

</script>

<style>

.modal-enter-active,
.modal-leave-active{
transition:all .25s ease;
}

.modal-enter-from{
opacity:0;
transform:scale(.9);
}

.modal-leave-to{
opacity:0;
transform:scale(.9);
}

</style>